/**
 * Comment Section Component
 * 
 * Shows comments on feedback and allows logged-in users to comment.
 * Guests clicking comment form triggers auth modal.
 */
import { useState, useEffect } from '@wordpress/element';
import { __ } from '@wordpress/i18n';

const CommentSection = ({ feedbackId, isLoggedIn, currentUser, onAuthRequired, onCommentAdded }) => {
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newComment, setNewComment] = useState('');
    const [submitting, setSubmitting] = useState(false);

    // Get config from frontend or admin context
    const config = window.chaFeedbackFrontend || window.chaFeedbackConfig || {};
    
    // Get nonce with fallback
    const getNonce = () => {
        return config.nonce || (typeof wpApiSettings !== 'undefined' && wpApiSettings.nonce) || '';
    };

    // Build the correct REST URL
    const getRestUrl = () => {
        return config.restUrl || '/wp-json/changeloger/v1/feedback';
    };

    useEffect(() => {
        fetchComments();
    }, [feedbackId]);

    const fetchComments = async () => {
        try {
            const response = await fetch(
                `${getRestUrl()}/${feedbackId}/comments`,
                { 
                    credentials: 'same-origin',
                    headers: { 'X-WP-Nonce': getNonce() } 
                }
            );
            const data = await response.json();
            setComments(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Failed to fetch comments:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!newComment.trim()) return;
        
        // If not logged in, trigger auth modal
        if (!isLoggedIn) {
            if (onAuthRequired) onAuthRequired();
            return;
        }

        setSubmitting(true);
        try {
            const response = await fetch(
                `${getRestUrl()}/${feedbackId}/comments`,
                {
                    method: 'POST',
                    credentials: 'same-origin',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-WP-Nonce': getNonce()
                    },
                    body: JSON.stringify({ content: newComment })
                }
            );

            if (response.ok) {
                const comment = await response.json();
                setComments(prev => [...prev, comment]);
                setNewComment('');
                // Notify parent of new comment with the server-provided count.
                if (onCommentAdded) onCommentAdded(comment.comment_count);
            }
        } catch (error) {
            console.error('Failed to add comment:', error);
        } finally {
            setSubmitting(false);
        }
    };

    // Handle form focus for guests - trigger auth modal
    const handleFormFocus = () => {
        if (!isLoggedIn && onAuthRequired) {
            onAuthRequired();
        }
    };

    if (loading) {
        return <div className="cha-comments-loading">{__( 'Loading comments...', 'changeloger' )}</div>;
    }

    return (
        <div className="cha-comments-section">
            {comments.length === 0 ? (
                <p className="cha-no-comments">{__( 'No comments yet.', 'changeloger' )}</p>
            ) : (
                <ul className="cha-comments-list">
                    {comments.map(comment => (
                        <li key={comment.id} className="cha-comment">
                            {comment.avatar_url && (
                                <img src={comment.avatar_url} alt="" className="cha-avatar" />
                            )}
                            <div className="cha-comment-content">
                                <strong>{comment.author || __( 'Anonymous', 'changeloger' )}</strong>
                                <span className="cha-comment-date">
                                    {new Date(comment.date).toLocaleDateString()}
                                </span>
                                <p>{comment.content}</p>
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            {/* Comment form - always visible, triggers auth for guests on focus/submit */}
            <form className="cha-comment-form" onSubmit={handleSubmit}>
                <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    onFocus={handleFormFocus}
                    placeholder={__( 'Add a comment...', 'changeloger' )}
                    rows={2}
                />
                <button 
                    type="submit" 
                    disabled={submitting || !newComment.trim()}
                    className="cha-btn cha-btn-primary"
                >
                    {submitting ? __( 'Posting...', 'changeloger' ) : __( 'Post Comment', 'changeloger' )}
                </button>
            </form>
        </div>
    );
};

export default CommentSection;
